/* eslint-disable @dhl/generic/no-hardcoded-translations */
import type { MouseEvent, PropsWithChildren } from 'react'
import { BareButton } from '@dhl-ui/components'
import { type PageMenuItem } from './types'

type Props = PropsWithChildren<
  Pick<PageMenuItem, 'link' | 'isActive' | 'isDisabled'> & {
    /**
     * Unique identifier
     */
    id: string
    /**
     * Flag whether it is for a submenu item or not
     * @default false
     */
    isSubMenuItem?: boolean
    /**
     * When our user selects a menu item
     */
    onClick: (event: MouseEvent<HTMLElement>) => void

    isExpanded?: boolean
    isCollapsedMenuOpenForItem?: boolean
  }
>

export const SideMenuItem = ({
  link,
  isActive,
  isDisabled,
  id,
  onClick,
  children,
  isSubMenuItem = false,
  isExpanded,
  isCollapsedMenuOpenForItem,
}: Props) => {
  const black = 'grey.700'
  const testId = `page-side-menu-${id}`
  const activeColor = 'primary.main'
  const color = isActive ? activeColor : black

  return (
    <BareButton
      component="a"
      href={link}
      onClick={onClick}
      data-testid={testId}
      disabled={isDisabled}
      sx={({ spacing, misc }) => ({
        color,
        mb: 0.25,
        borderRadius: misc.borderRadius(2),
        bgcolor: isActive ? 'primary.states.hover' : undefined,
        textWrap: 'nowrap',
        overflow: 'hidden',
        position: 'relative',
        fontWeight: isActive ? 'bold' : undefined,
        ...(isSubMenuItem
          ? {
              p: spacing(1, 2, 1, 4.5),
              boxShadow: isActive ? `inset 2px 0 0 0 ${activeColor}` : 'none',
              minHeight: spacing(6),
              display: 'flex',
            }
          : {
              p: spacing(1, 2),
              minHeight: spacing(7),
              justifyContent: 'flex-start',
            }),

        '&:hover': {
          bgcolor: isActive ? 'components.navbar.selectedHover' : 'action.hover',
          opacity: 1,
          borderColor: isActive ? activeColor : 'grey.300',
        },
        '&:focus': {
          ...(isSubMenuItem ? {} : { bgcolor: isActive ? 'components.navbar.selected' : undefined }),
          opacity: 1,
          borderColor: isActive ? activeColor : 'grey.300',
        },
        '&.Mui-disabled': {
          bgcolor: 'action.disabledBackground',
        },
        '&.Mui-focusVisible': {
          bgcolor: isActive ? 'components.navbar.selectedHover' : 'action.hover',
          opacity: 1,
          borderColor: isActive ? activeColor : 'grey.300',
        },
      })}
      aria-current={isActive ? 'page' : undefined}
      // only for main menu
      {...(isSubMenuItem
        ? {}
        : {
            'aria-haspopup': !isExpanded,
            'aria-controls': isCollapsedMenuOpenForItem ? 'submenu-menu' : undefined,
            'aria-expanded': isCollapsedMenuOpenForItem ? 'true' : undefined,
          })}
    >
      {children}
    </BareButton>
  )
}
